import { reportError } from "../../../utils/Instrumentation";
import React, { useMemo } from "react";
import { View, Text, StyleSheet } from "react-native";
import {
  createHomeScreenOverlayContainerStyle,
} from "../HomeScreen.style";
import { CIItem } from "../types";
import LHOverlay from "../../../components/LHOverlay";
import { formatDate } from "../../../utils/dateFormatter";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";
import type { ThemeColors } from "../../../theme";


interface CIItemsOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  ciData: {
    counts: {
      total: number;
      onTrack: number;
      offTrack: number;
    };
    columns: string[];
    data: CIItem[];
  };
  selectedCiStatus: 'On Track' | 'Off Track' | 'All';
}

const CIItemsOverlay: React.FC<CIItemsOverlayProps> = ({
  visible,
  onDismiss,
  ciData,
  selectedCiStatus,
}) => {
  const { colors } = useTheme();
  const overlayContainerStyle = useMemo(
    () => createHomeScreenOverlayContainerStyle(colors),
    [colors]
  );
  const localStyles = useMemo(() => createLocalStyles(colors), [colors]);
  const items = selectedCiStatus === 'All' 
    ? ciData.data 
    : ciData.data.filter((item: CIItem) => item.Status === selectedCiStatus);

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title={`CI Items - ${selectedCiStatus}`}
      containerStyle={overlayContainerStyle}
      headerPaddingHorizontal={24}
      hideFooter={true}
      size="large"
    >
      <View style={localStyles.container}>
        {items.length === 0 ? (
          <Text style={localStyles.emptyText}>No items to display</Text>
        ) : (
          items.map((item: CIItem, idx: number) => (
            <View
              key={idx}
              style={[
                localStyles.card,
                idx === items.length - 1 ? { marginBottom: -20 } : undefined,
              ]}
            >
              <View style={localStyles.cardInner}>
                {/* Stacked layout: each header followed by its detail */}
                <View style={localStyles.stackRow}>
                  <Text style={localStyles.columnHeader}>Continuous Improvement Opportunity</Text>
                  <Text style={localStyles.columnDetail} numberOfLines={2}>{item['Continuous Improvement Opportunity']}</Text>
                </View>

                <View style={localStyles.stackRow}>
                  <Text style={localStyles.columnHeader}>Solution Description</Text>
                  <Text style={localStyles.columnDetail} numberOfLines={2}>{item['Solution description']}</Text>
                </View>

                <View style={localStyles.stackRow}>
                  <Text style={localStyles.columnHeader}>Target End Date</Text>
                  <Text style={localStyles.columnDetail}>{formatDate(item['Target End Date'])}</Text>
                </View>
              </View>
            </View>
          ))
        )}
      </View>
    </LHOverlay>
  );
};

export default CIItemsOverlay;

const createLocalStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      paddingHorizontal: 16,
      paddingBottom: 16,
    },
    emptyText: {
      color: colors.text.primary,
      fontSize: 14,
      padding: 16,
      fontFamily: FontFamily.regular,
    },
    card: {
      backgroundColor: colors.background.surface,
      borderRadius: 8,
      padding: 12,
      marginBottom: 12,
      borderWidth: 1,
      borderColor: colors.border.default,
      shadowColor: '#005cbc',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 24,
      elevation: 3,
    },
    cardInner: {
      padding: 4,
    },
    cardTitle: {
      color: colors.text.primary,
      fontWeight: '700',
      fontSize: 14,
      marginBottom: 6,
      fontFamily: FontFamily.bold,
    },
    cardDetail: {
      color: colors.text.secondary,
      fontSize: 14,
      marginBottom: 8,
      fontFamily: FontFamily.regular,
    },
    cardDate: {
      color: colors.text.secondary,
      fontSize: 14,
      fontWeight: '400',
      fontFamily: FontFamily.regular,
    },
    stackRow: {
      marginBottom: 10,
    },
    columnHeader: {
      color: colors.text.primary,
      fontSize: 14,
      fontWeight: '700',
      marginBottom: 4,
      fontFamily: FontFamily.bold,
    },
    columnDetail: {
      color: colors.text.secondary,
      fontSize: 14,
      fontWeight: '400',
      fontFamily: FontFamily.regular,
    },
  });
